/**
 * The Weather page's forecast days, from the API: up to seven days, each with its daytime and its night as the server says
 * them (W24 Job D: the reading in the reader's unit, the sky in the reader's language, its icon key, the weekday's short
 * name), and the National Weather Service office that issued the forecast. The server groups the forecast's periods into
 * days (services/weather/forecastDays); the app groups nothing.
 *
 * Endpoint: GET /weather/forecast/user?lang=&unit= → { success, forecast, office, now, days }, or for a garden the weather
 * service cannot place yet (B44) { success: true, forecast: null, state: 'no_forecast_yet', message }, the message being the
 * server's words in the gardener's language. Behind gardenContext: in a shared garden it is the owner's place. The raw
 * forecast and `now` it still carries are not read (the weather now is the header answer's, api/header).
 *
 * Who uses it: features/weather useWeatherDays only, which keeps it under [...WEATHER_FORECAST_QUERY_KEY, zip, language]
 * (['weather', 'forecast', zip, 'es']) for the header answer's keepMinutes.
 *
 * @module weather
 */

import { apiClient } from './apiClient';
import type { SkyKey } from './header';

/** A day's daytime or night, as the server says it. */
export interface ForecastPart {
  /** The reading in the reader's unit, "72°F"; null when the forecast has none. */
  display: string | null;
  /** The sky in the reader's language. */
  words: string;
  /** The sky's icon key (sun by day, moon by night, by the period's own). */
  icon: SkyKey;
}

/** One day of the forecast, as the server groups it. */
export interface ForecastDay {
  /** "YYYY-MM-DD", in the forecast's own time zone. */
  date: string;
  /** The weekday's short name in the reader's language, "Sat" or "sáb". */
  label: string;
  /** Its daytime, or null (today, in the evening). */
  day: ForecastPart | null;
  /** Its night, or null. */
  night: ForecastPart | null;
}

/** The office that issued the forecast; absent outside the US. */
export interface ForecastOffice {
  /** Its identifier, "RAH". */
  id?: string;
  /** Its place, "Raleigh, NC". */
  name?: string;
}

/** What the Weather page's read gives back: the days and the office, or the server's words for why there are none yet. */
export interface ForecastReply {
  /** The days, up to seven; empty when the garden has no forecast yet. */
  days: ForecastDay[];
  /** The issuing office; null when not sent. */
  office: ForecastOffice | null;
  /** The server's sentence for a garden with no forecast yet (B44), in the gardener's language; null otherwise. */
  noForecastMessage: string | null;
}

/**
 * Fetches the forecast's days for the garden being tended.
 *
 * @param lang - The reader's language.
 * @param unit - The reader's temperature unit.
 * @returns The days and the office, or no days and the server's sentence saying why when the weather service cannot place
 *   the garden yet.
 * @throws {Error} If the request fails, or the server answers success false (no postcode, no forecast); its own error text.
 */
export async function getForecastDays(lang: string, unit: 'F' | 'C'): Promise<ForecastReply> {
  const response = await apiClient.get('/weather/forecast/user', { params: { lang, unit } });
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to fetch weather forecast');
  }
  if (response.data.state === 'no_forecast_yet') {
    return { days: [], office: null, noForecastMessage: String(response.data.message ?? '') || null };
  }
  return { days: Array.isArray(response.data.days) ? response.data.days : [], office: response.data.office ?? null, noForecastMessage: null };
}
